import { useState, useSyncExternalStore } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { BackupKind } from '@/domain/types';
import { CloudIcon, CloudOffIcon, ExternalLinkIcon, SparkleIcon } from '../icons';
import { STORAGE, S } from '../strings';
import { useCompany } from '../hooks/useCompany';
import { useAiStatus } from '../hooks/useAiStatus';
import { useBackupLog, useStorageStatus } from '../hooks/useStorageStatus';
import { getDriveActions, subscribeDriveActions } from '../hooks/driveActions';
import { remoteStore } from '@/cloud/store';
import { useSettings } from '../hooks/useSettings';
import { Sheet } from '../components/Sheet';
import { Button } from '../components/Button';
import { Timeline, type TimelineItem } from '../components/Timeline';
import { Disclosure } from '../components/Disclosure';
import { formatBytes } from '../format';
import type { StorageStatusAction } from '../hooks/storageStatus';

export interface StatusPillProps {
  kind: 'drive' | 'ai';
  /** Icon + dot only; the text goes to aria-label and a tooltip. */
  compact?: boolean | undefined;
}

/** Drive or AI status pill; opens a sheet with details and actions. `aria-live` announces changes. */
export function StatusPill({ kind, compact = false }: StatusPillProps) {
  return kind === 'drive' ? <StoragePill compact={compact} /> : <AiPill compact={compact} />;
}

function PillButton({ tone, label, actionLabel, compact, icon: Icon, onClick }: { tone: string; label: string; actionLabel?: string | null | undefined; compact: boolean; icon: typeof CloudIcon; onClick: () => void }) {
  const text = actionLabel ? `${label} · ${actionLabel}` : label;
  return (
    <button
      type="button"
      className={['status-pill', `status-pill-${tone}`, compact ? 'status-pill-compact' : ''].filter(Boolean).join(' ')}
      onClick={onClick}
      aria-haspopup="dialog"
      aria-label={compact ? text : undefined}
      data-tooltip={compact ? text : undefined}
    >
      <span className="status-pill-dot" aria-hidden="true" />
      <Icon size={16} />
      {!compact ? (
        <span className="status-pill-text" aria-live="polite">
          <span className="truncate">{label}</span>
          {actionLabel ? <span className="status-pill-action">{actionLabel}</span> : null}
        </span>
      ) : (
        <span className="visually-hidden" aria-live="polite">
          {text}
        </span>
      )}
    </button>
  );
}

function useDriveActions() {
  return useSyncExternalStore(subscribeDriveActions, getDriveActions, getDriveActions);
}

/**
 * The storage pill. `driveActions` are registered by the Drive flows and are
 * Google-specific — connect and sign-in open a Google popup — so they are only
 * used while Drive is the active backend. Any other provider is sent to
 * Mer › Lagring, which owns its own connect flow. A manual backup is
 * provider-neutral (`backupScheduler.runNow`) and works either way.
 */
function StoragePill({ compact }: { compact: boolean }) {
  const { company } = useCompany();
  const { status, state, providerLabel } = useStorageStatus(company?.id);
  const isDrive = (useSettings()?.cloud.provider ?? 'drive') === 'drive';
  const log = useBackupLog(company?.id, 8);
  const actions = useDriveActions();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<StorageStatusAction | 'backup'>(null);

  const run = (action: StorageStatusAction | 'backup') => {
    if (!company) return;
    // A collision between two devices is settled on the provider's own page.
    if (action === 'resolve') {
      setOpen(false);
      navigate(isDrive ? '/mer/lagring/drive' : '/mer/lagring/koofr');
      return;
    }
    // Connecting, signing in and retrying all go through Google here; on any
    // other backend they would open the wrong dialog entirely.
    const googleOnly = action === 'connect' || action === 'sign_in' || action === 'retry';
    const fn = !isDrive && googleOnly
      ? undefined
      : action === 'connect'
        ? actions.connect
        : action === 'sign_in'
          ? actions.signIn
          : action === 'retry'
            ? actions.retry
            : action === 'backup'
              ? actions.backupNow
              : undefined;
    if (!fn) {
      // Not Drive, or no implementation registered yet: Mer › Lagring owns it.
      setOpen(false);
      navigate('/mer/lagring');
      return;
    }
    setBusy(action);
    // The implementation must call requestAccessToken synchronously; we do not await before calling it.
    Promise.resolve(fn(company.id)).finally(() => setBusy(null));
  };

  const items: TimelineItem[] = log.map((e) => ({
    id: e.id,
    at: e.at,
    title: e.ok ? `${kindLabel(e.kind)} sparad${e.bytes ? ` · ${formatBytes(e.bytes)}` : ''}` : `${kindLabel(e.kind)} misslyckades`,
    detail: e.error ?? undefined,
    tone: e.ok ? 'ok' : 'danger',
  }));
  // Built by the adapter: a Drive folder id is not a URL on any other backend,
  // and a Koofr folder id is a path.
  const folderUrl = state?.folders?.company ? (remoteStore()?.folderUrl(state.folders.company) ?? null) : null;
  const Icon = status.kind === 'disconnected' || status.kind === 'error' || status.kind === 'conflict' ? CloudOffIcon : CloudIcon;

  return (
    <>
      <PillButton tone={status.tone} label={status.label} actionLabel={status.actionLabel} compact={compact} icon={Icon} onClick={() => setOpen(true)} />
      <Sheet open={open} onClose={() => setOpen(false)} title={providerLabel} subtitle={status.label}>
        <div className="stack-l">
          <div className="row">
            {status.action ? (
              <Button variant="primary" onClick={() => run(status.action)} loading={busy === status.action}>
                {status.actionLabel}
              </Button>
            ) : (
              <Button variant="primary" onClick={() => run('backup')} loading={busy === 'backup'} disabled={!state?.folders}>
                {STORAGE.backupNow}
              </Button>
            )}
            {folderUrl ? (
              <a href={folderUrl} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-m">
                <ExternalLinkIcon size={18} />
                <span className="btn-label">{STORAGE.openFolder(providerLabel)}</span>
              </a>
            ) : null}
          </div>
          {status.kind === 'disconnected' ? (
            <p className="text-small text-2">Bokföringen finns bara i den här webbläsaren tills en lagring är ansluten. Bokföringslagen kräver att den sparas i sju år.</p>
          ) : null}
          <section aria-label={STORAGE.history} className="stack-s">
            <h3 className="label-xs">{STORAGE.history}</h3>
            <Timeline items={items} emptyText="Ingen säkerhetskopia ännu." />
          </section>
          <Disclosure label={STORAGE.lawTitle} size="s">
            <p className="text-small">{STORAGE.lawBody(providerLabel)}</p>
          </Disclosure>
          <Link to="/mer/lagring" className="text-small" onClick={() => setOpen(false)}>
            Alla inställningar för lagring
          </Link>
        </div>
      </Sheet>
    </>
  );
}

function kindLabel(kind: BackupKind): string {
  switch (kind) {
    case 'auto':
      return 'Automatisk kopia';
    case 'manual':
      return 'Manuell kopia';
    case 'monthly':
      return 'Månadsarkiv';
    case 'year_end':
      return 'Bokslutsarkiv';
    default: {
      const exhaustive: never = kind;
      throw new Error(`Unknown backup kind ${String(exhaustive)}`);
    }
  }
}

function AiPill({ compact }: { compact: boolean }) {
  const ai = useAiStatus();
  const [open, setOpen] = useState(false);
  const tone = ai.configured ? 'ok' : 'grey';
  return (
    <>
      <PillButton tone={tone} label={ai.label} actionLabel={ai.configured ? null : 'Anslut'} compact={compact} icon={SparkleIcon} onClick={() => setOpen(true)} />
      <Sheet open={open} onClose={() => setOpen(false)} title="AI" subtitle={ai.label}>
        <div className="stack">
          {ai.configured ? (
            <p>
              {ai.providerLabel}
              {ai.model ? ` · ${ai.model}` : ''}. Nyckeln sparas bara i den här webbläsaren och skickas bara till {ai.providerLabel}.
            </p>
          ) : (
            <p>Utan AI väntar kvittona på att du fyller i dem själv. Anslut Gemini, OpenAI eller en egen server under Mer › AI.</p>
          )}
          <div className="row">
            <Link to="/mer/ai" className="btn btn-primary btn-m" onClick={() => setOpen(false)}>
              <span className="btn-label">{ai.configured ? 'Inställningar för AI' : 'Anslut AI'}</span>
            </Link>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              {S.close}
            </Button>
          </div>
        </div>
      </Sheet>
    </>
  );
}
